import { Classification } from "@/components/Classification";
import { LoadingButton } from "@/components/LoadingButton";
import { CardTableLoader } from "@/features/workspace/widgets/Components";
import styled from "@emotion/styled";
import {
  type OpportunityAlert,
  type WidgetSize,
  acknowledgeAlert,
} from "@repo/api";
import {
  Badge,
  Table as SkapaTable,
  TableBody,
  Text,
  Variables,
  documentCheckmarkIcon,
} from "@repo/skapa";
import { T, t } from "@repo/translation";
import {
  CoWorkerName,
  LocalizedDateAndTime,
  ProjectTitle,
  translateAlerts,
  translateProjectStatus,
  useSettings,
  useToast,
} from "@repo/ui";
import { getErrorMessage, maskName, trackAnalytics } from "@repo/utils";
import { useNavigate } from "react-router-dom";

import { getAlertColor, getFirstUnacknowldgedAlert } from "./utils";

const CompactTable = styled(SkapaTable)`
  && tbody td {
    padding-top: ${Variables.space50};
    padding-bottom: ${Variables.space50};
  }
  && tbody td:nth-child(1),
  && tbody td:nth-child(2) {
    padding-inline-end: ${Variables.space75};
    width: 1%;
    white-space: nowrap;
  }
`;

const ClickableRow = styled.tr`
  cursor: pointer;
`;

const SecondRow = styled.div`
  color: ${Variables.colourTextAndIcon2};
`;

export const Table = ({
  isLoading,
  alerts,
  size,
}: {
  isLoading: boolean;
  alerts: OpportunityAlert[];
  size: WidgetSize;
}) => {
  if (isLoading) {
    return <CardTableLoader />;
  }

  return (
    <CompactTable fullWidth>
      <TableBody striped>
        {alerts.map((alert) => (
          <AlertRow key={alert.id} size={size} alert={alert} />
        ))}
      </TableBody>
    </CompactTable>
  );
};

const AlertRow = ({
  alert,
  size,
}: {
  alert: OpportunityAlert;
  size: WidgetSize;
}) => {
  const { updateToast } = useToast();
  const navigate = useNavigate();
  const { customerMode } = useSettings();

  const alertToShow = getFirstUnacknowldgedAlert(alert?.alertsList);

  if (!alert.opportunity) {
    return null;
  }

  return (
    <ClickableRow
      onClick={() => {
        navigate(`/opportunity/${alert.opportunity.opportunityId}/`);
      }}
    >
      <td>
        <LoadingButton
          size="small"
          iconOnly
          ssrIcon={documentCheckmarkIcon}
          type="tertiary"
          title={t("action.acknowledge")}
          onClick={() => {
            trackAnalytics("acknowledge_alert");
            if (alertToShow?.id !== undefined) {
              return acknowledgeAlert(alertToShow?.id, true)
                .then(() => {
                  updateToast({
                    id: alertToShow?.id,
                    title: t("prompt.alert_acknowledged"),
                    button: {
                      title: t("action.undo"),

                      action: () =>
                        acknowledgeAlert(alertToShow?.id, false).then(() => {
                          updateToast({
                            title: t("prompt.alert_acknowledged_undo"),
                          });
                        }),
                    },
                  });
                })
                .catch((err) => {
                  updateToast({
                    title: t("prompt.generic_error"),
                    message: getErrorMessage(err),
                    type: "error",
                  });
                });
            }
          }}
        />
      </td>
      <td>
        <Classification
          id={alert.opportunity.opportunityId.toString()}
          classification={alert.opportunity.classification}
        />
      </td>
      <td>
        <Text textStyle="Heading.XS">
          {customerMode
            ? maskName(alert.opportunity.customerName)
            : alert.opportunity.customerName}
        </Text>
        <SecondRow>
          <Text textStyle="Body.S" noWrap>
            <ProjectTitle
              title={alert.opportunity.title}
              customerName={alert.opportunity.customerName}
            />
          </Text>
        </SecondRow>
      </td>
      <td>
        <Text textStyle="Heading.XS">
          {translateProjectStatus(alert.opportunity.status)}
        </Text>
        <SecondRow title="Last updated">
          <Text textStyle="Body.S" noWrap>
            <LocalizedDateAndTime date={alert.opportunity.updatedAt ?? ""} />
          </Text>
        </SecondRow>
      </td>
      {size === "L" ? (
        <td>
          <Text textStyle="Heading.XS">
            <T translationKey="label.created_by" />
          </Text>
          <SecondRow title="Last updated">
            <CoWorkerName email={alertToShow?.createdBy ?? t("label.system")} />
          </SecondRow>
        </td>
      ) : null}
      <td style={{ textAlign: "end" }}>
        <Badge
          colour={getAlertColor(alertToShow)}
          label={translateAlerts(alertToShow?.alertType)}
          size="small"
        />
      </td>
    </ClickableRow>
  );
};
